// Session token store — single source of truth for the bearer session token
// used to authenticate against all three hallpass-backend Cloud Run services
// and the Socket.io handshake (`{ auth: { token } }`).
//
// Framework-free by design: no React imports, so it can be read from
// anywhere (including outside component tree, e.g. socket setup).

const STORAGE_KEY = 'hallpass.sessionToken';

let cachedToken: string | null = null;
try {
  cachedToken = localStorage.getItem(STORAGE_KEY);
} catch {
  // localStorage unavailable (e.g. private browsing, SSR) — stay in-memory only.
  cachedToken = null;
}

export function getToken(): string | null {
  return cachedToken;
}

export function setToken(token: string): void {
  cachedToken = token;
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Ignore storage errors — in-memory cache still works for this session.
  }
}

export function clearToken(): void {
  cachedToken = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors.
  }
}

// Cross-tab sync: the `storage` event fires only in OTHER tabs (never the tab
// that made the change), so this keeps a tab's in-memory cache in step when a
// login/logout happens elsewhere. The writing tab already updates cachedToken
// via setToken/clearToken. A `null` key means localStorage.clear() was called.
try {
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY || event.key === null) {
        // newValue is null when the key was removed (logout) or cleared.
        cachedToken = event.newValue;
      }
    });
  }
} catch {
  // No window / addEventListener unavailable (e.g. SSR) — stay single-tab.
}
